import { type ReactNode } from 'react';

export type Announcement = {
	text: ReactNode;
	delay?: number;
	clearAfter?: number;
};

export type AnnouncementOwner = symbol;

interface StatusAnnouncementStore {
	announce: (owner: AnnouncementOwner, announcement: Announcement) => void;
	cancelPendingAnnouncement: (owner?: AnnouncementOwner) => boolean;
	getServerSnapshot: () => ReactNode;
	getSnapshot: () => ReactNode;
	resetStatus: (owner?: AnnouncementOwner) => void;
	subscribe: (listener: () => void) => () => void;
}

let statusText: ReactNode = '';
let announceTimeout: ReturnType<typeof setTimeout> | null = null;
let clearTimeoutRef: ReturnType<typeof setTimeout> | null = null;
let activeOwner: AnnouncementOwner | null = null;
const listeners = new Set<() => void>();

function notify(): void {
	listeners.forEach((listener) => listener());
}

function cancelPendingAnnouncement(owner?: AnnouncementOwner): boolean {
	if (owner !== undefined && activeOwner !== owner) {
		return false;
	}

	if (announceTimeout === null) {
		return false;
	}

	clearTimeout(announceTimeout);
	announceTimeout = null;
	return true;
}

function cancelClear(owner?: AnnouncementOwner): void {
	if (owner !== undefined && activeOwner !== owner) {
		return;
	}

	if (clearTimeoutRef !== null) {
		clearTimeout(clearTimeoutRef);
		clearTimeoutRef = null;
	}
}

function publish(owner: AnnouncementOwner, text: ReactNode, clearAfter?: number): void {
	if (activeOwner !== owner) {
		return;
	}

	announceTimeout = null;
	statusText = text;
	notify();

	if (clearAfter !== undefined) {
		clearTimeoutRef = setTimeout(() => {
			if (activeOwner !== owner) {
				return;
			}

			statusText = '';
			activeOwner = null;
			clearTimeoutRef = null;
			notify();
		}, clearAfter);
	}
}

function announce(owner: AnnouncementOwner, { text, delay = 0, clearAfter }: Announcement): void {
	activeOwner = owner;
	cancelPendingAnnouncement();
	cancelClear();

	if (delay === 0) {
		publish(owner, text, clearAfter);
	} else {
		announceTimeout = setTimeout(() => publish(owner, text, clearAfter), delay);
	}
}

function resetStatus(owner?: AnnouncementOwner): void {
	if (owner !== undefined && activeOwner !== owner) {
		return;
	}

	cancelPendingAnnouncement(owner);
	cancelClear(owner);
	statusText = '';
	activeOwner = null;
	notify();
}

function subscribe(listener: () => void): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

function getSnapshot(): ReactNode {
	return statusText;
}

function getServerSnapshot(): ReactNode {
	// Keep the server snapshot empty so SSR always renders the persistent live region without stale text.
	return '';
}

const statusAnnouncementStore: StatusAnnouncementStore = {
	announce,
	cancelPendingAnnouncement,
	getServerSnapshot,
	getSnapshot,
	resetStatus,
	subscribe,
};

export default statusAnnouncementStore;
